import { ArrowLeft, Camera, ChevronRight, Filter, HardDrive, Monitor, Plus, Search, Server, SlidersHorizontal, X, type LucideIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { CreateDeviceRequest } from '../api/devices';
import { AddDeviceDialog } from '../components/devices/AddDeviceDialog';
import type { DeviceTypeKey } from '../components/devices/deviceTypes';
import { DeviceCard } from '../components/DeviceCard';
import { DeviceListItem } from '../components/DeviceListItem';
import { MobileFilterSelect } from '../components/MobileFilterSelect';
import { LoadingState, StatePanel } from '../components/StatePanel';
import { useAuth } from '../context/AuthContext';
import { useMonitoring } from '../context/MonitoringContext';
import type { DeviceStatus, DeviceType } from '../types';
import { recorderToDevice } from '../utils/device';

type TypeFilter = 'all' | DeviceType;
type StatusFilter = 'all' | DeviceStatus;

const typeFilters: { value: TypeFilter; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'camera', label: 'Камеры' },
  { value: 'recorder', label: 'DVR / NVR' },
  { value: 'computer', label: 'Компьютеры' },
  { value: 'server', label: 'Серверы' },
];

const statusFilters: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Все статусы' },
  { value: 'online', label: 'В сети' },
  { value: 'offline', label: 'Не в сети' },
  { value: 'warning', label: 'Внимание' },
  { value: 'critical', label: 'Критично' },
];

const deviceCategories: { type: DeviceType; label: string; icon: LucideIcon; tone: string; to: string }[] = [
  { type: 'camera', label: 'Камеры', icon: Camera, tone: 'blue', to: '/devices?type=camera' },
  { type: 'recorder', label: 'Видеорегистраторы', icon: HardDrive, tone: 'green', to: '/devices?type=recorder' },
  { type: 'computer', label: 'Компьютеры', icon: Monitor, tone: 'violet', to: '/devices?type=computer' },
  { type: 'server', label: 'Серверы', icon: Server, tone: 'cyan', to: '/devices?type=server' },
];

const formatDeviceCount = (count: number) => {
  const remainder = count % 100;
  if (remainder >= 11 && remainder <= 14) return `${count} устройств`;
  if (count % 10 === 1) return `${count} устройство`;
  if (count % 10 >= 2 && count % 10 <= 4) return `${count} устройства`;
  return `${count} устройств`;
};

export function DevicesPage() {
  const { profile } = useAuth();
  const { snapshot, loadState, refresh, createDevice, isLiveData } = useMonitoring();
  const [addDialogType, setAddDialogType] = useState<DeviceTypeKey | null | undefined>(undefined);
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const type = (params.get('type') || 'all') as TypeFilter;
  const status = (params.get('status') || 'all') as StatusFilter;
  const sort = params.get('sort') || 'severity';

  const allDevices = useMemo(
    () => snapshot
      ? snapshot.devices.some((device) => device.type === 'recorder')
        ? snapshot.devices
        : isLiveData
          ? snapshot.devices
          : [...snapshot.devices, recorderToDevice(snapshot.recorder)]
      : [],
    [snapshot, isLiveData],
  );

  const updateParam = (key: string, value: string, defaultValue = 'all') => {
    const next = new URLSearchParams(params);
    if (!value || value === defaultValue) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const closeAddDevice = useCallback(() => setAddDialogType(undefined), []);
  const handleAddDevice = useCallback(
    async (request: CreateDeviceRequest) => { await createDevice(request); },
    [createDevice],
  );
  const openAddSelector = useCallback(() => setAddDialogType(null), []);

  const visibleDevices = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('ru-RU');
    const severityOrder: Record<DeviceStatus, number> = {
      critical: 0,
      offline: 1,
      warning: 2,
      unauthorized: 3,
      maintenance: 4,
      updating: 5,
      disabled: 6,
      online: 7,
    };

    return allDevices
      .filter((device) => type === 'all' || device.type === type)
      .filter((device) => status === 'all' || device.status === status)
      .filter((device) =>
        !normalizedQuery ||
        [device.name, device.ip, device.location, device.vendor, device.model]
          .some((value) => value.toLocaleLowerCase('ru-RU').includes(normalizedQuery)),
      )
      .sort((left, right) => {
        if (sort === 'name') return left.name.localeCompare(right.name, 'ru');
        if (sort === 'health') return left.health - right.health;
        return severityOrder[left.status] - severityOrder[right.status];
      });
  }, [allDevices, query, sort, status, type]);

  if (loadState === 'loading' || loadState === 'idle') return <LoadingState />;
  if (!snapshot) return <StatePanel kind="error" title="Устройства недоступны" description="Не удалось получить список устройств." actionLabel="Повторить" onAction={() => void refresh()} />;

  const selectedCategory = type === 'all'
    ? 'Все устройства'
    : deviceCategories.find((category) => category.type === type)?.label || 'Устройства';
  const showCategories = !params.get('view') && !query && type === 'all' && status === 'all' && sort === 'severity';
  if (showCategories) {
    return (
      <div className="page-stack device-categories-page">
        <header className="device-categories-header">
          <div>
            <h1>Устройства</h1>
            <p>{formatDeviceCount(allDevices.length)}</p>
          </div>
          {profile?.role === 'Administrator' && (
            <button className="device-categories-header__add" type="button" onClick={openAddSelector} aria-label="Добавить устройство">
              <Plus size={24} strokeWidth={1.8} aria-hidden="true" />
            </button>
          )}
        </header>

        <div className="device-categories-search">
          <label className="search-field">
            <Search size={18} aria-hidden="true" />
            <span className="sr-only">Поиск устройств</span>
            <input
              type="search"
              value={query}
              onChange={(event) => updateParam('q', event.target.value, '')}
              placeholder="Поиск устройств"
            />
          </label>
          <button
            className="device-categories-search__filter"
            type="button"
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('view', 'list');
              setParams(next, { replace: true });
            }}
            aria-label="Показать фильтры устройств"
          >
            <SlidersHorizontal size={20} aria-hidden="true" />
          </button>
        </div>

        <section className="device-category-grid" aria-label="Разделы устройств">
          {deviceCategories.map(({ type: categoryType, label, icon: Icon, tone, to }) => {
            const count = allDevices.filter((device) => device.type === categoryType).length;
            return (
              <Link key={categoryType} className={`device-category-card device-category-card--${tone}`} to={to}>
                <span className="device-category-card__icon" aria-hidden="true"><Icon size={27} strokeWidth={2} /></span>
                <span className="device-category-card__copy">
                  <strong>{label}</strong>
                  <span>{formatDeviceCount(count)}</span>
                </span>
                <ChevronRight className="device-category-card__chevron" size={21} strokeWidth={1.8} aria-hidden="true" />
              </Link>
            );
          })}
        </section>

        <AddDeviceDialog
          open={addDialogType !== undefined}
          initialType={addDialogType}
          recorders={snapshot.devices.filter((device) => device.type === 'recorder')}
          onClose={closeAddDevice}
          onSubmit={handleAddDevice}
        />
      </div>
    );
  }

  const showCategoryList = !params.get('view') && type !== 'all' && !query && status === 'all' && sort === 'severity';
  if (showCategoryList) {
    return (
      <div className="page-stack device-category-list-page">
        <header className="device-category-list-header">
          <Link className="device-list-header__back" to="/devices" aria-label="Вернуться к разделам устройств">
            <ArrowLeft size={22} strokeWidth={1.8} aria-hidden="true" />
          </Link>
          <div>
            <h1>{selectedCategory}</h1>
            <p>{formatDeviceCount(visibleDevices.length)}</p>
          </div>
          {profile?.role === 'Administrator' && (
            <button className="device-category-list-header__add" type="button" onClick={() => setAddDialogType((type as DeviceTypeKey | 'all') === 'all' ? null : (type as DeviceTypeKey))} aria-label={`Добавить: ${selectedCategory}`}>
              <Plus size={24} strokeWidth={1.8} aria-hidden="true" />
            </button>
          )}
        </header>

        {visibleDevices.length ? (
          <section className="device-grid device-grid--list" aria-label={`Список: ${selectedCategory}`}>
            {visibleDevices.map((device) => <DeviceListItem key={device.id} device={device} />)}
          </section>
        ) : (
          <StatePanel kind="empty" title={`${selectedCategory} не найдены`} description="В этом разделе пока нет подключённых устройств." actionLabel="Вернуться к разделам" onAction={() => setParams({}, { replace: true })} />
        )}

        <AddDeviceDialog
          open={addDialogType !== undefined}
          initialType={addDialogType}
          recorders={snapshot.devices.filter((device) => device.type === 'recorder')}
          onClose={closeAddDevice}
          onSubmit={handleAddDevice}
        />
      </div>
    );
  }

  const activeFilters = Number(type !== 'all') + Number(status !== 'all') + Number(Boolean(query));

  return (
    <div className="page-stack devices-page device-list-page">
      <header className="device-list-header">
        <Link className="device-list-header__back" to="/devices" aria-label="Вернуться к разделам устройств">
          <ArrowLeft size={22} strokeWidth={1.8} aria-hidden="true" />
        </Link>
        <div>
          <h1>{selectedCategory}</h1>
          <p>{formatDeviceCount(visibleDevices.length)}</p>
        </div>
      </header>

      <section className="filter-panel" aria-label="Фильтры устройств">
        <div className="filter-panel__top">
          <label className="search-field">
            <Search size={18} aria-hidden="true" />
            <span className="sr-only">Поиск устройств</span>
            <input
              type="search"
              value={query}
              onChange={(event) => updateParam('q', event.target.value, '')}
              placeholder="Название, IP или площадка…"
            />
            {query && <button type="button" onClick={() => updateParam('q', '', '')} aria-label="Очистить поиск"><X size={16} /></button>}
          </label>
          <label className="select-field">
            <span className="sr-only">Сортировка</span>
            <SlidersHorizontal size={17} aria-hidden="true" />
            <select value={sort} onChange={(event) => updateParam('sort', event.target.value, 'severity')}>
              <option value="severity">Сначала проблемные</option>
              <option value="name">По имени</option>
              <option value="health">По health score</option>
            </select>
          </label>
        </div>

        <div className="mobile-filter-grid">
          <MobileFilterSelect
            label="Тип"
            value={type}
            options={typeFilters}
            onChange={(value) => updateParam('type', value)}
          />
          <MobileFilterSelect
            label="Статус"
            value={status}
            options={statusFilters}
            onChange={(value) => updateParam('status', value)}
          />
        </div>

        <div className="filter-groups">
          <div className="filter-scroll" role="group" aria-label="Тип устройства">
            {typeFilters.map((item) => {
              const count = item.value === 'all' ? allDevices.length : allDevices.filter((device) => device.type === item.value).length;
              return (
                <button key={item.value} className={`filter-chip ${type === item.value ? 'is-active' : ''}`} type="button" onClick={() => updateParam('type', item.value)} aria-pressed={type === item.value}>
                  {item.label} <span>{count}</span>
                </button>
              );
            })}
          </div>
          <div className="filter-scroll" role="group" aria-label="Статус устройства">
            {statusFilters.map((item) => {
              const count = item.value === 'all' ? allDevices.length : allDevices.filter((device) => device.status === item.value).length;
              return (
                <button key={item.value} className={`filter-chip filter-chip--status ${status === item.value ? 'is-active' : ''}`} type="button" onClick={() => updateParam('status', item.value)} aria-pressed={status === item.value}>
                  {item.value !== 'all' && <i className={`dot dot--${item.value}`} aria-hidden="true" />}
                  {item.label} <span>{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="filter-summary">
          <span><Filter size={15} aria-hidden="true" /> Найдено: <strong>{visibleDevices.length}</strong></span>
          {activeFilters > 0 && (
            <button className="text-button" type="button" onClick={() => setParams({}, { replace: true })}>Сбросить фильтры ({activeFilters})</button>
          )}
        </div>
      </section>

      {visibleDevices.length ? (
        <section className="device-grid device-grid--list" aria-label="Список устройств">
          {visibleDevices.map((device) => <DeviceCard key={device.id} device={device} variant="list" />)}
        </section>
      ) : (
        <StatePanel kind="empty" title="Устройства не найдены" description="Измените условия поиска или сбросьте фильтры." actionLabel="Сбросить фильтры" onAction={() => setParams({}, { replace: true })} />
      )}
    </div>
  );
}
